import { useState, useEffect } from 'react';
import { observer } from 'mobx-react';

import OnboardingBlock from 'pages/Connection/components/OnboardingBlock';
import { about } from 'pages/Connection/components/AboutCompany';
import { appInfoInst } from 'pages/Connection/components/AppInfo';
import { companySignatoryInst } from 'pages/Connection/components/CompanySignatory';
import { contactPersonInst } from 'pages/Connection/components/ContactPerson';
import { corparateDoc } from 'pages/Connection/components/CorporateDocument';
import { director } from 'pages/Connection/components/Director';
import connectSources from 'store/ConnectSources';
import appStore from 'store/App';
import userStore from 'store/User';
import { Status } from 'store/Applaud';
import { getOnboardingStatus } from 'utils/userStatus';

import s from './styles.module.scss';

const InfoScreen = observer(() => {
  const [sourcesLoaded, setLoaded] = useState(false);
  // Мемоизируем статус онбординга чтобы не пересчитывать каждый рендер
  const [onboardingStatus, setOnboardingStatus] = useState(
    getOnboardingStatus(),
  );
  const isCompanyReviewing = userStore.me?.company?.status === 'in-review';

  // Обновляем статус онбординга при изменении данных
  useEffect(() => {
    setOnboardingStatus(getOnboardingStatus());
  }, [userStore.onboardingSteps, appStore.isLoadedMainInfo]);

  useEffect(() => {
    (async () => {
      try {
        // Оставляем только то что специфично для InfoScreen и не дублируется с App.tsx
        await connectSources.getTotalAccountsWithCredentials();

        setLoaded(true);
      } catch (error) {
        console.error('InfoScreen initialization error:', error);
        // Показываем уведомление об ошибке вместо падения приложения
        appStore.setNotification({
          type: 'error',
          title: 'Ошибка загрузки данных',
          message: 'Попробуйте обновить страницу',
          width: 400,
        });
        setLoaded(true); // Все равно показываем интерфейс
      }
    })();
  }, []);

  useEffect(() => {
    if (
      userStore.me?.company.status !== Status.new &&
      userStore.me?.company.status !== Status.rejected
    ) {
      appStore.showAccountsDrawer = false;
    }
  }, [userStore.me?.company.status]);

  const { isIE, isAllCompleted } = onboardingStatus;

  // Показываем лоадер пока не загружены основные данные
  if (
    !appStore.isLoadedMainInfo ||
    userStore.isLoadingOnboarding ||
    !userStore.onboardingSteps
  ) {
    return (
      <div className={s.container}>
        <div style={{ padding: '20px', textAlign: 'center' }}>
          Loading onboarding data...
        </div>
      </div>
    );
  }

  return (
    <div className={s.container}>
      {/* App/Game Info Block */}
      <OnboardingBlock
        title="App/Game Info"
        description="Please provide information about your applications or/and games."
        isCompleted={onboardingStatus.isAppInfoCompleted}
        isAllCompleted={isAllCompleted}
        screenName="appInfo"
      />

      {/* Basic Company Info Block */}
      <OnboardingBlock
        title="Basic company info"
        description="We will review the data and then send you the contracts for signature."
        isCompleted={onboardingStatus.isBasicCompanyInfoCompleted}
        isAllCompleted={isAllCompleted}
        screenName="aboutCompany"
      />

      {/* Company Signatory Block */}
      <OnboardingBlock
        title="Company Signatory"
        description="Please provide the details of the person who is entitled to sign, execute and approve business contracts on behalf of your company."
        isCompleted={onboardingStatus.isCompanySignatoryCompleted}
        isAllCompleted={isAllCompleted}
        screenName="companySignatory"
      />

      {/* Contact Person Block */}
      <OnboardingBlock
        title="Contact Person"
        description="Please provide the details of your contact person"
        isCompleted={onboardingStatus.isContactPersonCompleted}
        isAllCompleted={isAllCompleted}
        screenName="personInfo"
      />

      {/* Corporate Documents Block - только для partnership и corporation */}
      <OnboardingBlock
        title="Set of corporate documents"
        description={
          !about.isPersonalDataValid || !about.isCompanyInfoValid
            ? 'This section will be available after completing the sections above.'
            : 'Anti-money laundering and terrorist financing regulations require GamesBoost42 LTD to fulfill certain Know Your Customer identification requirements. The information provided here to Gamesboost42 LTD and its affiliates shall only be used to satisfy those requirements.'
        }
        isCompleted={onboardingStatus.isCorporateDocumentsCompleted}
        isAllCompleted={isAllCompleted}
        screenName="corporateDocs"
        buttonText="Upload documents"
        showWhen={['partnership', 'corporation'].includes(
          about.values.details.company_form ?? '',
        )}
      />

      {/* UBOs&Directors Block */}
      <OnboardingBlock
        title="UBOs&Directors"
        description="Anti-money laundering and terrorist financing regulations require GamesBoost42 LTD to fulfill certain Know Your Customer identification requirements. The information provided here to Gamesboost42 LTD and its affiliates shall only be used to satisfy those requirements."
        isCompleted={onboardingStatus.isDirectorsCompleted}
        isAllCompleted={isAllCompleted}
        screenName="directors"
        buttonText="Upload documents"
        disabled={
          !(
            onboardingStatus.isAppInfoCompleted &&
            onboardingStatus.isBasicCompanyInfoCompleted &&
            onboardingStatus.isCompanySignatoryCompleted &&
            onboardingStatus.isContactPersonCompleted &&
            (isIE ? true : onboardingStatus.isCorporateDocumentsCompleted)
          )
        }
      />
    </div>
  );
});

export default InfoScreen;
